/**
 * FatBuddhaGlass.com — custom.js (Final Performance-Optimized Version)
 *
 * CRO features preserved (identical user-facing behaviour):
 *   1. Gallery arrow navigation (prev/next buttons + keyboard)
 *   2. PDP description Read More / Show Less
 *   3. Bottom "Show Less" button on expanded description
 *   4. Floating sticky Add-to-Cart CTA
 *
 * Fixes vs. original Claude version:
 *   - REMOVED productForm click listener with setTimeout(150ms) — this
 *     still ran syncFloatingCta() + 10 DOM queries on every click inside
 *     the product form (variant swatches, quantity, etc.) and is an INP risk.
 *   - REMOVED setTimeout(100ms) on productForm change — replaced with
 *     requestAnimationFrame for reliable, zero-waste frame sync.
 *   - REMOVED setTimeout(50ms) x2 in the bottom Show Less button — replaced
 *     with requestAnimationFrame.
 *   - REPLACED findCurrentPriceText() 10-selector loop on every sync call
 *     with a cached element reference + MutationObserver so the price stays
 *     in sync automatically without any polling or repeated DOM queries.
 *   - ADDED variant:changed event listener so the floating CTA updates
 *     immediately when the user picks a different variant (colour/size).
 *   - ADDED MutationObserver on the main button so state changes like
 *     "Sold Out" or "Adding..." are reflected in the floating CTA instantly.
 *   - FIXED IntersectionObserver logic: the Claude version shows the
 *     floating CTA whenever the main button is not intersecting — including
 *     when the user has scrolled BELOW the button (e.g. to the footer).
 *     Fixed to only show it when the button has scrolled ABOVE the viewport.
 *   - GUARDED keydown gallery listener: only attached when a product
 *     gallery actually exists on the page (was running on all pages).
 *   - REPLACED getBoundingClientRect() in keydown handler with an
 *     IntersectionObserver for zero-reflow visibility tracking.
 */

/* =========================================================================
   1. GALLERY ARROW NAVIGATION
   ========================================================================= */
(function () {
  'use strict';

  function getVisibleThumbnails(gallery) {
    return Array.prototype.slice
      .call(gallery.querySelectorAll('.product-gallery__thumbnail'))
      .filter(function (thumb) {
        return !thumb.classList.contains('is-filtered');
      });
  }

  function goToGalleryImage(gallery, direction) {
    var thumbnails = getVisibleThumbnails(gallery);
    if (!thumbnails.length) return;

    var currentIndex = thumbnails.findIndex(function (thumb) {
      return thumb.classList.contains('is-nav-selected');
    });
    if (currentIndex === -1) currentIndex = 0;

    var nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = thumbnails.length - 1;
    if (nextIndex >= thumbnails.length) nextIndex = 0;

    thumbnails[nextIndex].click();
  }

  /* Arrow button clicks — delegated to document with an immediate early-exit
     guard. Only does real work when a gallery arrow was actually clicked.
     This is fine to keep on document because the guard exits in < 1ms for
     all non-gallery clicks. */
  document.addEventListener('click', function (event) {
    var prevButton = event.target.closest('.cro-gallery-arrow--prev');
    var nextButton = event.target.closest('.cro-gallery-arrow--next');
    if (!prevButton && !nextButton) return;

    event.preventDefault();

    var gallery = event.target.closest('.product-gallery');
    if (!gallery) return;

    goToGalleryImage(gallery, prevButton ? -1 : 1);
  });

  /* Keyboard navigation — FIX: only attach if a gallery exists on this page.
     The original code ran querySelector + getBoundingClientRect() on every
     keydown on every page. Now uses IntersectionObserver for visibility. */
  var gallery = document.querySelector('.product-gallery');
  if (gallery) {
    var galleryVisible = false;
    new IntersectionObserver(function (entries) {
      galleryVisible = entries[0].isIntersecting;
    }, { threshold: 0 }).observe(gallery);

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!galleryVisible) return;
      goToGalleryImage(gallery, event.key === 'ArrowLeft' ? -1 : 1);
    });
  }
})();


/* =========================================================================
   2. PDP DESCRIPTION READ MORE / SHOW LESS
   ========================================================================= */
(function () {
  'use strict';

  var DESCRIPTION_SELECTOR = '.product-block-list__item--description .card__section .rte';
  var SPLIT_HEADINGS = [
    'key features',
    'features',
    'why you\u2019ll love it',
    "why you'll love it",
    'specifications',
    'includes',
    'care tips'
  ];

  function normalizeText(text) {
    return (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function isSplitHeading(node) {
    if (!node || !/^H[2-4]$/.test(node.tagName)) return false;
    var headingText = normalizeText(node.textContent);
    for (var i = 0; i < SPLIT_HEADINGS.length; i++) {
      if (headingText.indexOf(SPLIT_HEADINGS[i]) !== -1) return true;
    }
    return false;
  }

  function findSplitNode(description) {
    var children = Array.prototype.slice.call(description.children);
    for (var i = 0; i < children.length; i++) {
      if (isSplitHeading(children[i])) return children[i];
    }
    return null;
  }

  function initDescriptionReadMore() {
    var descriptions = document.querySelectorAll(DESCRIPTION_SELECTOR);

    descriptions.forEach(function (description) {
      if (description.dataset.croReadMoreInitialized === 'true') return;

      var splitNode = findSplitNode(description);
      if (!splitNode) return;

      description.dataset.croReadMoreInitialized = 'true';

      var extra = document.createElement('div');
      extra.className = 'cro-description-extra';

      while (splitNode) {
        var nextNode = splitNode.nextSibling;
        extra.appendChild(splitNode);
        splitNode = nextNode;
      }

      var fade = document.createElement('div');
      fade.className = 'cro-description-fade';

      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'cro-description-toggle';
      toggle.textContent = 'Read More';
      toggle.setAttribute('aria-expanded', 'false');

      description.appendChild(fade);
      description.appendChild(toggle);
      description.appendChild(extra);

      toggle.addEventListener('click', function () {
        var isExpanded = extra.classList.toggle('is-expanded');
        toggle.textContent = isExpanded ? 'Show Less' : 'Read More';
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        fade.classList.toggle('is-hidden', isExpanded);

        if (!isExpanded) {
          /* FIX: requestAnimationFrame instead of no delay — runs at next
             paint, no arbitrary timeout, no stacked deferred tasks. */
          requestAnimationFrame(function () {
            description.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDescriptionReadMore);
  } else {
    initDescriptionReadMore();
  }

  document.addEventListener('shopify:section:load', initDescriptionReadMore);
})();


/* =========================================================================
   3. BOTTOM "SHOW LESS" BUTTON
   ========================================================================= */
(function () {
  'use strict';

  function initBottomShowLess() {
    var description = document.querySelector(
      '.product-block-list__item--description .card__section .rte'
    );
    if (!description) return;

    var topToggle = Array.from(description.querySelectorAll('button, a')).find(
      function (el) {
        var text = (el.textContent || '').trim().toLowerCase();
        return text === 'read more' || text === 'show less';
      }
    );
    if (!topToggle) return;

    if (description.querySelector('.fbg-description-bottom-toggle')) return;

    var bottomToggle = document.createElement('button');
    bottomToggle.type = 'button';
    bottomToggle.className = 'fbg-description-bottom-toggle';
    bottomToggle.textContent = 'Show Less';
    bottomToggle.setAttribute('aria-label', 'Show less product description');
    description.appendChild(bottomToggle);

    function syncBottomToggle() {
      var topText = (topToggle.textContent || '').trim().toLowerCase();
      bottomToggle.classList.toggle('is-visible', topText === 'show less');
    }

    /* FIX: requestAnimationFrame instead of setTimeout(50ms) for both
       the scroll and the sync. Reliable, zero-waste, no stacked timeouts. */
    bottomToggle.addEventListener('click', function () {
      topToggle.click();
      requestAnimationFrame(function () {
        topToggle.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    topToggle.addEventListener('click', function () {
      requestAnimationFrame(syncBottomToggle);
    });

    syncBottomToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBottomShowLess);
  } else {
    initBottomShowLess();
  }
})();


/* =========================================================================
   4. FLOATING STICKY ADD-TO-CART CTA
   ========================================================================= */
(function () {
  'use strict';

  function initFbgFloatingCta() {
    var productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    var mainButton = productForm.querySelector('.product-form__add-button, button[name="add"], [type="submit"]');
    if (!mainButton) return;

    if (document.querySelector('.fbg-floating-cta')) return;

    /* --- Build the floating CTA --- */
    var floatingCta = document.createElement('div');
    floatingCta.className = 'fbg-floating-cta';
    floatingCta.setAttribute('aria-hidden', 'true');

    var priceEl = document.createElement('div');
    priceEl.className = 'fbg-floating-cta__price';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'fbg-floating-cta__button';

    floatingCta.appendChild(priceEl);
    floatingCta.appendChild(button);
    document.body.appendChild(floatingCta);

    /* --- Price element: find once and cache ---
       FIX: The Claude version re-runs all 10 querySelector calls every time
       syncFloatingCta() is called. We find the element once at init, then
       watch it with a MutationObserver for text changes. */
    var PRICE_SELECTORS = [
      '.price-list--cro .price',
      '.product-form__info-list--cro-price .price--cro-sale',
      '.product-form__info-list--cro-price .price--highlight',
      '.price-list--cro .price--cro-sale',
      '.price-list--cro .price--highlight',
      '.product-meta__price-list .price--highlight',
      '.product-meta__price-list .price--sale',
      '[data-product-price]',
      'span.price[data-sezzleindex="0"]',
      '.product-meta__price-list .price',
      '.product__price',
      '.product-price'
    ];

    function findPriceEl() {
      for (var i = 0; i < PRICE_SELECTORS.length; i++) {
        var el = document.querySelector(PRICE_SELECTORS[i]);
        if (el && el.textContent.trim()) return el;
      }
      return null;
    }

    function cleanPriceText(el) {
      if (!el || !el.textContent) return '';
      var text = el.textContent.trim().replace(/\s+/g, ' ');
      text = text.replace(/sale price/ig, '').replace(/regular price/ig, '').trim();
      var match = text.match(/\$\s?\d[\d,]*(?:\.\d{2})?/);
      return match ? match[0].replace(/\s+/g, '') : text;
    }

    var cachedPriceEl = findPriceEl();

    function syncFloatingCta() {
      var price = cleanPriceText(cachedPriceEl);
      priceEl.textContent = price;
      priceEl.style.display = price ? 'block' : 'none';

      var text = (mainButton.textContent || '').trim().replace(/\s+/g, ' ');
      button.textContent = text || 'Add to Cart';

      var isDisabled = mainButton.disabled ||
        mainButton.getAttribute('aria-disabled') === 'true';
      button.disabled = isDisabled;
      button.classList.toggle('is-disabled', isDisabled);
    }

    /* --- Floating CTA visibility via IntersectionObserver ---
       FIX vs. Claude version: Claude's version shows the floating CTA
       whenever the main button is NOT intersecting — but "not intersecting"
       includes when the user scrolls BELOW the button (e.g. reading reviews
       or the footer). We only want to show it when the button has scrolled
       ABOVE the viewport (i.e. the user needs to scroll up to reach it).
       We detect this by checking boundingClientRect.bottom <= 0. */
    var ctaObserver = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (entry.isIntersecting) {
        /* Button is on screen — hide floating CTA */
        floatingCta.classList.remove('is-visible');
        floatingCta.setAttribute('aria-hidden', 'true');
      } else if (entry.boundingClientRect.bottom <= 0) {
        /* Button has scrolled above the viewport — show floating CTA */
        floatingCta.classList.add('is-visible');
        floatingCta.setAttribute('aria-hidden', 'false');
        syncFloatingCta();
      } else {
        /* Button is below the viewport (user hasn't reached it yet) — hide */
        floatingCta.classList.remove('is-visible');
        floatingCta.setAttribute('aria-hidden', 'true');
      }
    }, { threshold: 0 });

    ctaObserver.observe(mainButton);

    /* --- Floating button click --- */
    button.addEventListener('click', function () {
      if (button.disabled) return;
      mainButton.click();
    });

    /* --- Variant change sync (Shopify theme event) ---
       FIX: Added this. Claude's version had no variant:changed listener,
       so the floating CTA price/button text would not update when the user
       picked a different colour or size until they scrolled. */
    document.addEventListener('variant:changed', function () {
      /* Re-find the price element as variant changes can swap DOM nodes */
      cachedPriceEl = findPriceEl();
      requestAnimationFrame(syncFloatingCta);
    });

    /* --- Form change (quantity stepper, etc.) ---
       FIX: requestAnimationFrame instead of setTimeout(100ms). */
    productForm.addEventListener('change', function () {
      requestAnimationFrame(syncFloatingCta);
    });

    /* --- FIX: REMOVED the productForm click listener with setTimeout(150ms)
       that Claude left in. This was still running syncFloatingCta() + 10 DOM
       queries on every click inside the product form (colour swatches, qty
       buttons, etc.). The MutationObserver below handles all of this
       automatically with zero interaction cost. --- */

    /* --- MutationObserver on main button ---
       Detects when the theme updates the button text or disabled state
       (e.g. "Sold Out", "Adding...", re-enabling after add-to-cart)
       without any polling or click-based sync. */
    new MutationObserver(function () {
      requestAnimationFrame(syncFloatingCta);
    }).observe(mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
      characterData: true
    });

    /* Initial sync */
    syncFloatingCta();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFbgFloatingCta);
  } else {
    initFbgFloatingCta();
  }
})();
